import { CUMULATIVE_SIZE_CURRENCY } from '@velocity-exchange/common';
import { useEffect } from 'react';
import { Swap } from '@velocity-exchange/icons';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import MarketIcon from 'src/components/Utils/MarketIcon';
import TextButton from 'src/components/primitives/TextButton/TextButton';

interface Props {
	options: {
		value: CUMULATIVE_SIZE_CURRENCY;
		label: string;
	}[];
	value: CUMULATIVE_SIZE_CURRENCY;
	onChange: (val: CUMULATIVE_SIZE_CURRENCY) => void;
	isMini?: boolean;
}

export const OrderbookCurrencySwitch = ({
	options,
	value,
	onChange,
	isMini,
}: Props) => {
	const [storedCurrency, setStoredCurrency] = useSyncLocalStorage(
		'orderbook-currency-preference',
		CUMULATIVE_SIZE_CURRENCY.USD.toString()
	);

	useEffect(() => {
		const parsedStoredCurrency = parseInt(
			storedCurrency
		) as CUMULATIVE_SIZE_CURRENCY;
		// Validate that parsed value is a valid currency option
		const isValidCurrency = options.some(
			(option) => option.value === parsedStoredCurrency
		);
		if (!isValidCurrency) {
			return;
		}
		if (parsedStoredCurrency !== value) {
			onChange(parsedStoredCurrency);
		}
	}, [storedCurrency, value, onChange]);

	const selectedOptionLabel = (() => {
		const currentlySelectedOption = options.find(
			(option) => option.value === value
		);

		if (!currentlySelectedOption) {
			return null;
		}

		if (!isMini || value === CUMULATIVE_SIZE_CURRENCY.USD) {
			return currentlySelectedOption.label;
		}

		return (
			<MarketIcon
				marketSymbol={currentlySelectedOption.label}
				sizeClass="size-4"
			/>
		);
	})();

	const handleToggle = () => {
		const currentIndex = options.findIndex((option) => option.value === value);
		const nextIndex = (currentIndex + 1) % options.length;
		const nextCurrency = options[nextIndex]?.value ?? options[0]?.value;

		onChange(nextCurrency);
		setStoredCurrency(nextCurrency.toString());
	};

	return (
		<TextButton size="sm" onClick={handleToggle} rightIcon={Swap}>
			<span className="truncate">{selectedOptionLabel}</span>
		</TextButton>
	);
};
